import { uniqWith, once } from "lodash-comms";
import {
  ApiVersionError,
  AuthenticationError,
  PermissionError,
  TransactionConflictError,
  UnreachableCaseError,
  ValidationError,
} from "libs/errors";
import { formatResponseError } from "./api.service";
import { getOperationPointers } from "libs/transaction";
import { RecordMap, RecordPointer } from "libs/schema";
import { DeferredPromise, onlyCallFnOnceWhilePreviousCallIsPending, throttleAsyncFn, wait } from "libs/promise-utils";
import { MS_IN_MINUTE, MS_IN_SECOND } from "libs/date-helpers";
import { ClientEnvironment } from "./ClientEnvironment";
import { globalState } from "~/state/global.state";
import {
  BehaviorSubject,
  distinctUntilChanged,
  firstValueFrom,
  map,
  Observable,
  pairwise,
  share,
  switchMap,
} from "rxjs";
import { startWith } from "libs/rxjs-operators";
import { arrayChange } from "libs/array-utils";
import { LocalForageKVStore } from "~/utils/KVStore";
import { isDefined, isEqual } from "libs/predicates";
import { PartialAction, Action } from "libs/actions";
import { uuid } from "libs/uuid";
import { omit } from "lodash-es";

export type UnknownAction = Action<unknown>;

export type QueuedAction = {
  action: UnknownAction;
  deferred: DeferredPromise<void>;
};

export type DebouncedAction = {
  toQueue: QueuedAction;
  debounceTimeoutId: ReturnType<typeof setTimeout>;
  debounceMs: number;
  maxWaitTimeoutId: ReturnType<typeof setTimeout>;
  maxWaitMs: number;
};

export type EnqueueOptions = {
  debounce?: {
    key: string;
    /** Pass 0 to dequeue the action immediately and clear any existing debounce */
    debounceMs: number;
    /**
     * Pass 0 to dequeue the action immediately and clear any existing debounce. Otherwise,
     * the existing maxWaitMs, if any, will be preferred over this one.
     */
    maxWaitMs?: number;
  };
};

export type ActionQueueApi = Pick<
  ActionQueue,
  "init" | "dequeue" | "enqueue" | "isPendingWrite" | "isQueueEmpty" | "isQueueEmpty$" | "flush"
>;

export class ActionQueue {
  /** Key to use with the isLoading service to indicate that the tx queue is loading */
  static readonly isLoadingKey = "ActionQueue";
  /**
   * Key to use with the isLoading service to indicate that the tx queue is loading something
   * that is applicable in offline mode. This is a subset of the isLoadingKey.
   */
  static readonly isLoadingKeyOffline = "ActionQueue-offline";

  // We use localForage here, instead of localStorage, because it also works inside worker's
  static store = new LocalForageKVStore({
    namespace: "ActionQueue",
  });

  // number of seconds to wait before retrying after on error (will increase exponentially)
  private errorBackoffInSeconds: number;

  /** Thunks which are ready to be processed */
  queue: QueuedAction[] = [];

  debouncedActions = new Map<
    string, // debounce key
    DebouncedAction
  >();

  // Each record is allowed a single debounced tx type at a time. This is intentional since allowing
  // multiple types of debounced txs for a single record seems like it would be susceptible to bugs.
  pointersWithDebouncedAction = new Map<
    string, // record key
    string // debounce key
  >();

  // Note that sometimes we might increment pending writes for the same record more times
  // than we have pending operations for that record. I.e. the pending writes count should
  // be thought of as "Are there any pending writes? Yes or no."
  pendingWrites$ = new BehaviorSubject<Map<string, number>>(new Map());

  get pendingWrites() {
    return this.pendingWrites$.getValue();
  }

  actionStoreChanges$: Observable<UnknownAction[] | undefined>;

  constructor(
    public env: Pick<ClientEnvironment, "auth" | "api" | "network" | "logger" | "isLoading">,
    public props: {
      onWrite: (props: { recordMap: RecordMap }) => Promise<void>;
      onRollback: (props: { action: UnknownAction }) => Promise<void>;
      errorBackoffInSeconds?: number;
    },
  ) {
    this.env = {
      ...this.env,
      logger: this.env.logger.child({ name: "ActionQueue" }),
    };

    this.errorBackoffInSeconds = props.errorBackoffInSeconds ?? 10;

    this.actionStoreChanges$ = this.env.auth.currentUserId$.pipe(
      switchMap((currentUserId) => ActionQueue.store.getItem$<UnknownAction[]>(`actions-${currentUserId}`)),
      share(),
    );
  }

  init() {
    setInterval(this.logState.bind(this), MS_IN_MINUTE * 5);

    this.env.auth.currentUserId$.subscribe((currentUserId) => {
      this.loadFromOffline(currentUserId);
    });

    // Subscribe to storage changes so that we can be aware of pending writes in other tabs.
    // Note that if we increment pending writes for an action initiated in this tab multiple
    // times (e.g. once here and once below), that's ok.
    this.actionStoreChanges$
      .pipe(
        map((actions = []) => actions.flatMap((a) => a.operations.flatMap(getOperationPointers))),
        startWith(() => []),
        pairwise(),
        map(([prev, next]) => arrayChange(next, prev, isEqual)),
      )
      .subscribe(({ added, removed }) => {
        this.incPendingWrites(added);
        this.decPendingWrites(removed);
      });
  }

  isQueueEmpty() {
    return this.pendingWrites.size === 0;
  }

  isQueueEmpty$() {
    return this.pendingWrites$.pipe(
      map((pendingWrites) => pendingWrites.size === 0),
      distinctUntilChanged(),
    );
  }

  isPendingWrite(pointer: RecordPointer<any>) {
    const n = this.pendingWrites.get(pointerToKey(pointer)) || 0;
    return n > 0;
  }

  /**
   * Used to flush debounced actions and immediately start processing them. If
   * debounceKeys are supplied then only a debounced action matching that key will
   * be flushed. Otherwise, all debounced actions will be flushed.
   *
   * When flush is called, it will synchronously push debounced actions to the queue
   * (updating `queue`, `debouncedActions`, and `pointersWithDebouncedAction`) but it won't resolve
   * until the ActionQueue's offline store has been updated.
   */
  async flush(pointers?: RecordPointer[]) {
    if (pointers) {
      const debouncedKeys = new Set<string>();

      for (const pointer of pointers) {
        const pointerKey = pointerToKey(pointer);
        const debounceKey = this.pointersWithDebouncedAction.get(pointerKey);
        if (debounceKey) debouncedKeys.add(debounceKey);
        this.pointersWithDebouncedAction.delete(pointerKey);
      }

      if (!debouncedKeys.size) return;

      for (const debouncedKey of debouncedKeys) {
        const data = this.debouncedActions.get(debouncedKey);

        if (!data) {
          this.env.logger.warn({ debouncedKey }, `[flush] missing debouncedKey data`);
          continue;
        }

        this.queue.push(data.toQueue);
        clearTimeout(data.debounceTimeoutId);
        clearTimeout(data.maxWaitTimeoutId);
        this.debouncedActions.delete(debouncedKey);
      }
    } else {
      if (!this.debouncedActions.size) return;

      for (const data of this.debouncedActions.values()) {
        clearTimeout(data.debounceTimeoutId);
        clearTimeout(data.maxWaitTimeoutId);
        this.queue.push(data.toQueue);
      }

      this.debouncedActions.clear();
      this.pointersWithDebouncedAction.clear();
    }

    // We save for offline again because the order of this thunk may now be different vs
    // before.
    await this.saveState();
    this.dequeue();
  }

  /** Use `write(environment, { action })` instead of this function for optimistic updates. */
  async enqueue(action: UnknownAction, options?: EnqueueOptions) {
    const pointers = uniqWith(action.operations.flatMap(getOperationPointers), isEqual);

    let deferred: DeferredPromise<void>;

    if (!options?.debounce) {
      deferred = new DeferredPromise();
      this.incPendingWrites(pointers);
      this.env.isLoading.add(deferred.promise, { key: ActionQueue.isLoadingKey });

      // It's important that we flush any debounced updates before pushing this new action onto the
      // queue so that those updates happen before this action.
      this.flush(pointers);
      this.queue.push({ deferred, action });

      await this.saveState();
      this.dequeue();
      return deferred.promise;
    }

    const { debounce } = options;

    // Flush any pointers which have a pending action associated with a different debounce key
    const pointersToBeFlushed = pointers.filter((pointer) => {
      const pointerKey = pointerToKey(pointer);
      const existingDebounceKey = this.pointersWithDebouncedAction.get(pointerKey);
      return isDefined(existingDebounceKey) && existingDebounceKey !== debounce.key;
    });

    // No need to await saving the new state as we're about to update the state again
    this.flush(pointersToBeFlushed);

    pointers.forEach((pointer) => {
      const pointerKey = pointerToKey(pointer);
      // Mark this pointer as having a debounced action with the new key.
      this.pointersWithDebouncedAction.set(pointerKey, debounce.key);
    });

    const debouncedAction = this.debouncedActions.get(debounce.key);
    const onTimeout = once(() => this.flush(pointers));
    let maxWaitTimeoutId: ReturnType<typeof setTimeout>;
    let maxWaitMs: number;

    if (debouncedAction) {
      maxWaitMs = debouncedAction.maxWaitMs;
      maxWaitTimeoutId = debouncedAction.maxWaitTimeoutId;
      clearTimeout(debouncedAction.debounceTimeoutId);
      deferred = debouncedAction.toQueue.deferred;
    } else {
      maxWaitMs = debounce.maxWaitMs || MS_IN_SECOND * 30;
      maxWaitTimeoutId = setTimeout(onTimeout, maxWaitMs);
      deferred = new DeferredPromise();
      this.incPendingWrites(pointers);
    }

    this.env.isLoading.add(deferred.promise, { key: ActionQueue.isLoadingKey });

    const thunk: QueuedAction = { deferred, action };

    const debounceTimeoutId = setTimeout(onTimeout, debounce.debounceMs);

    this.debouncedActions.set(debounce.key, {
      toQueue: thunk,
      debounceTimeoutId,
      debounceMs: debounce.debounceMs,
      maxWaitTimeoutId,
      maxWaitMs,
    });

    if (debounce.debounceMs === 0 || debounce.maxWaitMs === 0) {
      await this.flush(pointers);
    } else {
      await this.saveState();
    }

    return deferred.promise;
  }

  dequeue = onlyCallFnOnceWhilePreviousCallIsPending(async () => {
    try {
      while (this.queue.length && this.env.network.isOnline()) {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        const thunk = this.queue[0]!;

        const result = await writeAction(this.env, {
          txId: uuid(),
          action: thunk.action,
          errorBackoffInSeconds: this.errorBackoffInSeconds,
          maxAttempts: 5,
        });

        if (result.type === "offline") break; // Try again later...

        this.queue.shift();
        await this.saveState();

        switch (result.type) {
          case "ok": {
            await this.resolve([thunk], result.recordMap);
            break;
          }
          case "error": {
            this.env.logger.fatal({ error: result.error }, "[ActionQueue] unexpected error in dequeue");

            globalState.getState().setError({
              message: getErrorMessage(this.queue.length),
              code: "TQ01",
            });

            await this.rollback(thunk, result.error);

            // We break out of the loop to avoid automatically trying the rest of the actions.
            // The other actions will be tried when the user reloads the page.
            break;
          }
          case "rollback": {
            await this.rollback(thunk, result.error);
            break;
          }
          default: {
            throw new UnreachableCaseError(result);
          }
        }
      }
    } catch (error) {
      globalState.getState().setError({
        message: getErrorMessage(this.queue.length),
        code: "TQ02",
      });

      this.env.logger.fatal({ error }, "[ActionQueue] unexpected error in dequeue");
    }
  });

  // we're intentionally passing the current user ID here instead of using getCurrentUserId
  // to make it clear that this method is impacted by the current user ID.
  async loadFromOffline(currentUserId: string | null) {
    const key = `actions-${currentUserId}`;

    const actions = await ActionQueue.store.getItem<UnknownAction[]>(key);

    if (!actions) return;

    if (this.queue.length || this.debouncedActions.size) {
      this.env.logger.error(
        { readyThunksCount: this.queue.length, debouncedThunksCount: this.debouncedActions.size },
        `[loadFromOffline] existing actions in memory already`,
      );
    }

    // When loading offline actions, we first clear any existing ones which are pending.
    this.queue = [];
    this.debouncedActions = new Map();

    for (const action of actions) {
      this.enqueue(action);
    }
  }

  saveState = (() => {
    const save = throttleAsyncFn(async () => {
      const currentUserId = this.env.auth.getCurrentUserId();

      // When saving actions for offline, we ignore the debounced state.
      // Under normal usage, these actions will be executed from memory, respecting the
      // debounced state. But if the client is closed and reopened, then we forget the debounced
      // state and attempt to execute all saved actions immediately.
      const actions = [
        ...this.queue.map((thunk) => thunk.action),
        ...Array.from(this.debouncedActions.values()).map(({ toQueue }) => toQueue.action),
      ];

      try {
        const nextChange = firstValueFrom(this.actionStoreChanges$);
        const changed = await ActionQueue.store.setItem(`actions-${currentUserId}`, actions);
        // Wait to resolve until after we've processed the emission of `ActionQueue.store.getItem$` above.
        // This is important so that, when rolling back a action, we process the rollback after we've decremented
        // any pending writes related to the action.
        if (changed) await nextChange;
      } catch (error) {
        this.env.logger.error({ error }, `[saveForOffline] error`);
      }
    });

    // If we called `isLoading.add()` inside the `save()` function, then the previous call would resolve,
    // setting the `ActionQueue.isLoadingKeyOffline` loading key to false, before the next call would
    // then be processed. The result would be that the `ActionQueue.isLoadingKeyOffline` key would
    // "flash" from `true -> false -> true`, which could potentially cause unexpected behavior if trigger
    // an event as soon as they key becomes `false`.
    return () =>
      this.env.isLoading.add(save(), {
        key: [ActionQueue.isLoadingKey, ActionQueue.isLoadingKeyOffline],
      });
  })();

  async resolve(queued: QueuedAction[], recordMap: RecordMap) {
    await this.props.onWrite({ recordMap });

    for (const data of queued) {
      const pointers = uniqWith(data.action.operations.flatMap(getOperationPointers), isEqual);
      this.decPendingWrites(pointers);
      data.deferred.resolve();
      this.env.logger.debug({ action: data.action }, `action successfully sent to server`);
    }
  }

  async rollback(queued: QueuedAction, error: Error) {
    const { action, deferred } = queued;
    // We want to decrement the pending writes before calling onRollback so that
    // we can check and see if a record still has pending writes inside the onRollback call.
    const pointers = uniqWith(action.operations.flatMap(getOperationPointers), isEqual);
    this.decPendingWrites(pointers);

    try {
      this.env.logger.warn({ action, error }, `action rejected by server`);
      await this.props.onRollback({ action });
      deferred.reject(error);
    } catch (error) {
      this.env.logger.fatal({ error }, "[rollback] error during rollback");

      globalState.getState().setError({
        message: "An unexpected error occurred while rolling back an action.",
        code: "TQ03",
      });

      // Note that we're intentionally not rejecting the promise in the case of an error on rollback.
      // This is so that any followup logic doesn't run.
    }
  }

  incPendingWrites(pointers: RecordPointer[]) {
    pointers.forEach((pointer) => {
      const key = pointerToKey(pointer);
      const n = this.pendingWrites.get(key) || 0;
      this.pendingWrites.set(key, n + 1);
    });

    this.pendingWrites$.next(this.pendingWrites);
  }

  decPendingWrites(pointers: RecordPointer[]) {
    pointers.forEach((pointer) => {
      const key = pointerToKey(pointer);
      const n = this.pendingWrites.get(key) || 0;

      if (n <= 1) {
        this.pendingWrites.delete(key);
      } else {
        this.pendingWrites.set(key, n - 1);
      }
    });

    this.pendingWrites$.next(this.pendingWrites);
  }

  async logState(label?: string) {
    const actions = await ActionQueue.store.getItem<UnknownAction[]>(`actions-${this.env.auth.getCurrentUserId()}`);

    if (!actions) return;

    if (!Array.isArray(actions)) {
      this.env.logger.error({ label, actions }, "[ActionQueue] unexpected store value");
      return;
    }

    if (actions.length < 20) return;

    this.env.logger.notice(
      {
        label,
        actionsCount: actions.length,
        // Theoretically a client could build up a huge list of actions and logging all
        // of them could take up a lot of bandwidth and resources. Instead, we just log the
        // first 5 actions. Theoretically these are the most important anyway since
        // they're processed in order. So if an error is occurring when the actions are
        // being processed, it would be the first action's fault.
        actionsSlice: actions.slice(0, 5),
      },
      "[ActionQueue] pending actions",
    );
  }
}

/* -----------------------------------------------------------------------------------------------*/

type WriteResponse =
  | { type: "ok"; recordMap: RecordMap }
  | { type: "offline" }
  | { type: "rollback"; error: Error }
  | { type: "error"; error: Error };

async function writeAction(
  environment: Pick<ClientEnvironment, "api" | "logger">,
  props: {
    txId: string;
    action: UnknownAction;
    errorBackoffInSeconds: number;
    maxAttempts: number;
  },
): Promise<WriteResponse> {
  const { txId, action, errorBackoffInSeconds, maxAttempts } = props;

  const payload = {
    txId,
    label: action.name,
    action: omit(action, "operations") as PartialAction<unknown>,
  };

  environment.logger.debug(payload, "[write] sending action to server");

  let attempts = 0;

  // Submit and retry.
  while (true) {
    attempts++;

    const response = await environment.api.writeAction(payload);

    if (response.status === 200) {
      return { type: "ok", recordMap: response.body.recordMap };
    }

    if (response.status === 0) {
      // Offline
      return { type: "offline" };
    }

    if (response.status === ValidationError.statusCode) {
      environment.logger.error(payload, `[write] ValidationError`);
      const error = new ValidationError(formatResponseError(response));
      return { type: "rollback", error };
    }

    if (response.status === PermissionError.statusCode) {
      environment.logger.error(payload, `[write] PermissionError`);
      const error = new PermissionError(formatResponseError(response));
      return { type: "rollback", error };
    }

    if (response.status === TransactionConflictError.statusCode) {
      if (attempts > maxAttempts) {
        environment.logger.warn(payload, `[write] TransactionConflictError`);
        const error = new TransactionConflictError(formatResponseError(response));
        return { type: "rollback", error };
      }

      // retry immediately
      continue;
    }

    if (response.status === ApiVersionError.statusCode) {
      // The API service is expected to handle these errors before resolving the request
      // and that involves a page reload. Reaching this branch is therefore unexpected.
      environment.logger.error(payload, `[write] ApiVersionError`);
      throw new Error(`[ActionQueue] [write] unexpected ApiVersionError`);
    }

    if (response.status === AuthenticationError.statusCode) {
      // The API service is expected to handle these errors before resolving the request
      // and that involves signing out, triggering a page reload. Reaching this branch
      // is therefore unexpected.
      environment.logger.error(payload, `[write] AuthenticationError`);
      throw new Error(`[ActionQueue] [write] unexpected AuthenticationError`);
    }

    // Errors handling
    // Below are unexpected errors which we attempt to retry

    if (attempts <= maxAttempts) {
      // Will retry after errorBackoffInSeconds (increasing exponentially)
      // With the default of 10 seconds, the retries will be at 10, 20, 40 and 80 seconds
      await wait(2 ** (attempts - 1) * MS_IN_SECOND * errorBackoffInSeconds);
      continue;
    }

    // Unknown error
    const error = new Error(response.status + ": " + response.body);
    environment.logger.error({ ...payload, error }, `[write] unknown error`);
    return { type: "error", error };
  }
}

/* -----------------------------------------------------------------------------------------------*/

function getErrorMessage(thunkCount: number) {
  return `
    An unexpected error occurred while syncing a recent change to the server.
    ${thunkCount && `Pausing further updates (updates remaining ${thunkCount}). `}
    Reload the page to try again.
  `;
}

function pointerToKey(pointer: RecordPointer<any>) {
  return `${pointer.table}:${pointer.id}`;
}

/* -----------------------------------------------------------------------------------------------*/
